import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DataTableAdvanced,
  type DataTableColumn,
  type DataTableFilter,
} from '@/components/ui/data-table-advanced'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useDrawer, DRAWER_PRESETS } from '@/components/ui/drawer'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { CreateSubscriptionDrawer } from '@/components/billing/CreateSubscriptionDrawer'
import { ManagePlansDrawer } from '@/components/billing/ManagePlansDrawer'
import {
  billingService,
  formatAmount,
  monthlyAmountCents,
  providerLabel,
  type BillingSubscription,
} from '@/services/billing'
import { format } from 'date-fns'
import { toast } from 'sonner'
import {
  Plus,
  MoreHorizontal,
  Copy,
  XCircle,
  Package,
  Calendar,
  Settings2,
} from 'lucide-react'

const statusVariant = (
  status: BillingSubscription['status']
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'active':
      return 'default'
    case 'past_due':
      return 'destructive'
    case 'pending_checkout':
      return 'secondary'
    default:
      return 'outline'
  }
}

export const BillingSubscriptions: React.FC = () => {
  const { openDrawer } = useDrawer()
  const { onMessage } = useDrawerMessaging('billing-subscriptions-page')
  const [subscriptions, setSubscriptions] = useState<BillingSubscription[]>([])
  const [loading, setLoading] = useState(true)
  const [cancelTarget, setCancelTarget] = useState<BillingSubscription | null>(null)

  const fetchSubscriptions = async () => {
    try {
      setLoading(true)
      setSubscriptions(await billingService.listSubscriptions())
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err)
      toast.error('Failed to load subscriptions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSubscriptions()
  }, [])

  useEffect(() => {
    return onMessage((message) => {
      if (message.action === 'refresh') fetchSubscriptions()
    })
  }, [onMessage])

  const active = subscriptions.filter((s) => s.status === 'active')
  const mrrCents = active.reduce((sum, s) => sum + monthlyAmountCents(s), 0)
  const pastDue = subscriptions.filter((s) => s.status === 'past_due').length
  const pending = subscriptions.filter((s) => s.status === 'pending_checkout').length

  const columns: DataTableColumn<BillingSubscription>[] = [
    {
      accessorKey: 'tenant_name',
      header: 'Tenant',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.tenant_name || row.original.tenant_id}</div>
      ),
    },
    {
      accessorKey: 'plan_name',
      header: 'Plan',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Package className="h-3 w-3 text-muted-foreground" />
          {row.original.plan_name || '—'}
        </div>
      ),
    },
    {
      accessorKey: 'provider',
      header: 'Processor',
      cell: ({ row }) => (
        <Badge variant="outline">{providerLabel(row.original.provider)}</Badge>
      ),
      filterFn: 'select',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={statusVariant(row.original.status)}>
          {row.original.status.replace('_', ' ')}
        </Badge>
      ),
      filterFn: 'select',
    },
    {
      accessorKey: 'amount_cents',
      header: 'Amount',
      cell: ({ row }) => (
        <span>
          {formatAmount(row.original.amount_cents, row.original.currency)}/
          {row.original.interval}
        </span>
      ),
    },
    {
      accessorKey: 'current_period_end',
      header: 'Renews',
      cell: ({ row }) =>
        row.original.current_period_end ? (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {format(new Date(row.original.current_period_end), 'MMM d, yyyy')}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
  ]

  const filters: DataTableFilter[] = [
    {
      column: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Pending checkout', value: 'pending_checkout' },
        { label: 'Past due', value: 'past_due' },
        { label: 'Canceled', value: 'canceled' },
      ],
    },
    {
      column: 'provider',
      type: 'select',
      label: 'Processor',
      options: [
        { label: 'Stripe', value: 'stripe' },
        { label: 'NMI', value: 'nmi' },
      ],
    },
  ]

  const handleNewSubscription = () => {
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: 'create-subscription',
      title: 'New Subscription',
      content: <CreateSubscriptionDrawer onSuccess={fetchSubscriptions} />,
      size: 'lg',
    })
  }

  const handleManagePlans = () => {
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: 'manage-plans',
      title: 'Subscription Plans',
      content: <ManagePlansDrawer onChanged={fetchSubscriptions} />,
      size: 'lg',
    })
  }

  const handleCopyCheckoutLink = async (sub: BillingSubscription) => {
    // The hosted URL isn't persisted; regenerating is a cancel+recreate, so we
    // surface the session ref for support and tell the admin what to do.
    if (sub.checkout_session_ref) {
      navigator.clipboard.writeText(sub.checkout_session_ref)
      toast.info(
        'Checkout session ref copied. To get a fresh link, cancel this pending subscription and create a new one.'
      )
    }
  }

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return
    const sub = cancelTarget
    setCancelTarget(null)
    try {
      await billingService.cancelSubscription(sub.id)
      toast.success(`Subscription for ${sub.tenant_name || 'tenant'} canceled`)
      fetchSubscriptions()
    } catch (err) {
      console.error('Failed to cancel subscription:', err)
      toast.error('Failed to cancel subscription')
    }
  }

  const renderActions = (sub: BillingSubscription) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        {sub.status === 'pending_checkout' && sub.checkout_session_ref && (
          <DropdownMenuItem onClick={() => handleCopyCheckoutLink(sub)}>
            <Copy className="mr-2 h-4 w-4" />
            Copy checkout ref
          </DropdownMenuItem>
        )}
        {sub.status !== 'canceled' && (
          <DropdownMenuItem
            className="text-destructive"
            onClick={() => setCancelTarget(sub)}
          >
            <XCircle className="mr-2 h-4 w-4" />
            Cancel subscription
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Subscriptions</h1>
          <p className="text-muted-foreground mt-1">
            Tenant subscriptions across all payment processors
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleManagePlans}>
            <Settings2 className="mr-2 h-4 w-4" />
            Manage Plans
          </Button>
          <Button onClick={handleNewSubscription}>
            <Plus className="mr-2 h-4 w-4" />
            New Subscription
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active</p>
              <p className="text-2xl font-bold">{active.length}</p>
            </div>
            <Badge variant="default" className="h-6">Active</Badge>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">MRR</p>
              <p className="text-2xl font-bold">{formatAmount(mrrCents)}</p>
            </div>
            <Package className="h-8 w-8 text-muted-foreground opacity-50" />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Past Due</p>
              <p className="text-2xl font-bold">{pastDue}</p>
            </div>
            <Badge variant="destructive" className="h-6">Past due</Badge>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Pending Checkout</p>
              <p className="text-2xl font-bold">{pending}</p>
            </div>
            <Badge variant="secondary" className="h-6">Pending</Badge>
          </div>
        </div>
      </div>

      <DataTableAdvanced
        columns={columns}
        data={subscriptions}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search subscriptions..."
        searchKeys={['tenant_name', 'plan_name']}
        filters={filters}
        showColumnVisibility={true}
        showPagination={true}
        pageSize={10}
        pageSizeOptions={[10, 20, 30, 50]}
        actions={renderActions}
        emptyMessage="No subscriptions yet. Create one with “New Subscription”."
      />

      <ConfirmDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null)
        }}
        title="Cancel subscription?"
        description={
          cancelTarget?.status === 'pending_checkout'
            ? `The pending checkout for ${cancelTarget?.tenant_name || 'this tenant'} will be invalidated.`
            : `${cancelTarget?.tenant_name || 'This tenant'} will not be billed again. This does not refund past invoices.`
        }
        confirmLabel="Cancel subscription"
        destructive
        onConfirm={handleConfirmCancel}
      />
    </div>
  )
}
